import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { StaffMasterDataService } from "lib-klab-sdks";
import { MasterDataPort } from "../../../app/ports/master-data/master-data.port";
import {
  CustomerUpsertRequest,
  MasterDataCustomer,
  MasterDataProduct,
  ProductUpsertRequest,
} from "../../../domain/models/master-data/business-data.model";
import {
  Currency,
  Country,
  CurrencyUpsertRequest,
  TaxUnit,
  TaxUnitUpsertRequest,
  PensionFund,
  PensionFundUpsertRequest,
  DynamicCatalog,
  DynamicCatalogItem,
  DynamicCatalogItemUpsertRequest,
  DynamicCatalogUpsertRequest,
  DocumentType,
  DocumentTypeUpsertRequest,
  GeoDepartment,
  GeoDistrict,
  GeoProvince,
  TaxRate,
  TaxRateCreateRequest,
  SctrProvider,
  SctrType,
  SctrUpsertRequest,
  BankEntity,
  BankEntityUpsertRequest,
  WorkerType,
  WorkerTypeUpsertRequest,
} from "../../../domain/models/master-data/reference-data.model";
import {
  Supplier,
  SupplierPage,
  SupplierPageRequest,
  SupplierListRequest,
  SupplierUpsertRequest,
  SupplierSunatResult,
  SupplierRequirementPageRequest,
  SupplierBankAccountPageRequest,
  SupplierDetailPage,
  SupplierRequirement,
  SupplierBankAccountInfo,
} from "../../../domain/models/master-data/supplier.model";
import { SupportDocumentType } from "../../../domain/models/master-data/support-document-type.model";
import { ExchangeRate } from "../../../domain/models/master-data/exchange-rate.model";
import {
  mapCatalog,
  mapCatalogItem,
  mapCatalogItemRequest,
  mapCatalogRequest,
  mapCurrency,
  mapCountry,
  mapCurrencyRequest,
  mapTaxUnit,
  mapTaxUnitRequest,
  mapPensionFund,
  mapPensionFundRequest,
  mapCustomer,
  mapCustomerRequest,
  mapDepartment,
  mapDistrict,
  mapDocumentType,
  mapDocumentTypeRequest,
  mapProvince,
  mapProduct,
  mapProductRequest,
  mapTaxRate,
  mapTaxRateRequest,
  mapSctr,
  mapSctrRequest,
  mapBankEntity,
  mapBankEntityRequest,
  mapWorkerType,
  mapWorkerTypeRequest,
  mapSupplier,
  mapSupplierRequest,
  mapSupportDocumentType,
} from "../../mappers/master-data/master-data.mapper";

@Injectable({ providedIn: "root" })
export class MasterDataPortAdapter implements MasterDataPort {
  getLatestExchangeRate(
    companyId: number,
    currencyFromId: number,
    currencyToId: number,
  ): Observable<ExchangeRate> {
    return this.api.getLatestExchangeRate(companyId, currencyFromId, currencyToId);
  }

  getSuppliers(request: SupplierListRequest = {}): Observable<readonly Supplier[]> {
    return this.api.getSuppliers(request).pipe(
      map(({ data }) => data.map(mapSupplier)),
    );
  }

  listSupportDocumentTypes(): Observable<SupportDocumentType[]> {
    return this.api.listSupportDocumentTypes().pipe(
      map(({ data }) => data.map(mapSupportDocumentType)),
    );
  }

  listSuppliers(request: SupplierPageRequest): Observable<SupplierPage> {
    return this.api.listSuppliers(request).pipe(
      map(({ data }) => ({
        ...data,
        content: data.content.map(mapSupplier),
      })),
    );
  }

  getSupplier(supplierId: number): Observable<Supplier> {
    return this.api.getSupplier(supplierId).pipe(map(({ data }) => mapSupplier(data)));
  }

  listSupplierRequirements(supplierId: number, request: SupplierRequirementPageRequest): Observable<SupplierDetailPage<SupplierRequirement>> {
    return this.api.listSupplierRequirements(supplierId, request).pipe(map(({ data }) => data));
  }

  listSupplierBankAccounts(supplierId: number, request: SupplierBankAccountPageRequest): Observable<SupplierDetailPage<SupplierBankAccountInfo>> {
    return this.api.listSupplierBankAccounts(supplierId, request).pipe(map(({ data }) => data));
  }

  findSupplierByDocument(documentNumber: string): Observable<Supplier> {
    return this.api.findSupplierByDocument(documentNumber).pipe(
      map(({ data }) => mapSupplier(data)),
    );
  }

  consultSupplierSunat(ruc: string): Observable<SupplierSunatResult> {
    return this.api.consultSupplierSunat(ruc).pipe(map(({ data }) => data));
  }

  createSupplier(request: SupplierUpsertRequest): Observable<Supplier> {
    return this.api.createSupplier(mapSupplierRequest(request)).pipe(
      map(({ data }) => mapSupplier(data)),
    );
  }

  updateSupplier(
    supplierId: number,
    request: SupplierUpsertRequest,
  ): Observable<Supplier> {
    return this.api.updateSupplier(supplierId, mapSupplierRequest(request)).pipe(
      map(({ data }) => mapSupplier(data)),
    );
  }

  deleteSupplier(supplierId: number): Observable<void> {
    return this.api.deleteSupplier(supplierId).pipe(map(() => undefined));
  }
  createCountry(request: any): Observable<any> { return this.api.createCountry(request); }
  updateCountry(id: number, request: any): Observable<any> { return this.api.updateCountry(id, request); }
  deleteCountry(id: number): Observable<void> { return this.api.deleteCountry(id); }

  listCountries(): Observable<Country[]> {
    return this.api.listCountries().pipe(map((items) => items.map(mapCountry)));
  }
  constructor(private readonly api: StaffMasterDataService) {}

  listDocumentTypes(): Observable<DocumentType[]> {
    return this.api
      .listDocumentTypes()
      .pipe(map((items) => items.map(mapDocumentType)));
  }

  createDocumentType(
    request: DocumentTypeUpsertRequest,
  ): Observable<DocumentType> {
    return this.api
      .createDocumentType(mapDocumentTypeRequest(request))
      .pipe(map(mapDocumentType));
  }

  updateDocumentType(
    id: number,
    request: DocumentTypeUpsertRequest,
  ): Observable<DocumentType> {
    return this.api
      .updateDocumentType(id, mapDocumentTypeRequest(request))
      .pipe(map(mapDocumentType));
  }

  deleteDocumentType(id: number): Observable<void> {
    return this.api.deleteDocumentType(id);
  }

  listCurrencies(activeOnly = true): Observable<Currency[]> {
    return this.api
      .listCurrencies(activeOnly)
      .pipe(map((items) => items.map(mapCurrency)));
  }

  createCurrency(request: CurrencyUpsertRequest): Observable<Currency> {
    return this.api
      .createCurrency(mapCurrencyRequest(request))
      .pipe(map(mapCurrency));
  }

  updateCurrency(
    idCurrency: number,
    request: CurrencyUpsertRequest,
  ): Observable<Currency> {
    return this.api
      .updateCurrency(idCurrency, mapCurrencyRequest(request))
      .pipe(map(mapCurrency));
  }

  deleteCurrency(idCurrency: number | string): Observable<void> {
    return this.api.deleteCurrency(idCurrency);
  }

  listTaxUnits(activeOnly = true): Observable<TaxUnit[]> {
    return this.api
      .listTaxUnits(activeOnly)
      .pipe(map((items) => items.map(mapTaxUnit)));
  }

  createTaxUnit(request: TaxUnitUpsertRequest): Observable<TaxUnit> {
    return this.api
      .createTaxUnit(mapTaxUnitRequest(request))
      .pipe(map(mapTaxUnit));
  }

  updateTaxUnit(
    taxUnitId: number,
    request: TaxUnitUpsertRequest,
  ): Observable<TaxUnit> {
    return this.api
      .updateTaxUnit(taxUnitId, mapTaxUnitRequest(request))
      .pipe(map(mapTaxUnit));
  }

  deleteTaxUnit(taxUnitId: number): Observable<void> {
    return this.api.deleteTaxUnit(taxUnitId);
  }

  listPensionFunds(activeOnly = true): Observable<PensionFund[]> {
    return this.api
      .listPensionFunds(activeOnly)
      .pipe(map((items) => items.map(mapPensionFund)));
  }

  createPensionFund(
    request: PensionFundUpsertRequest,
  ): Observable<PensionFund> {
    return this.api
      .createPensionFund(mapPensionFundRequest(request))
      .pipe(map(mapPensionFund));
  }

  updatePensionFund(
    pensionFundId: number,
    request: PensionFundUpsertRequest,
  ): Observable<PensionFund> {
    return this.api
      .updatePensionFund(pensionFundId, mapPensionFundRequest(request))
      .pipe(map(mapPensionFund));
  }

  deletePensionFund(pensionFundId: number): Observable<void> {
    return this.api.deletePensionFund(pensionFundId);
  }

  listDepartments(): Observable<GeoDepartment[]> {
    return this.api
      .listDepartments()
      .pipe(map((items) => items.map(mapDepartment)));
  }

  listProvinces(departmentCode: string): Observable<GeoProvince[]> {
    return this.api
      .listProvinces(departmentCode)
      .pipe(map((items) => items.map(mapProvince)));
  }

  listDistricts(provinceCode: string): Observable<GeoDistrict[]> {
    return this.api
      .listDistricts(provinceCode)
      .pipe(map((items) => items.map(mapDistrict)));
  }

  listTaxRates(activeOnly = true, code?: string): Observable<TaxRate[]> {
    return this.api
      .listTaxRates(activeOnly, code)
      .pipe(map((items) => items.map(mapTaxRate)));
  }

  createTaxRate(request: TaxRateCreateRequest): Observable<TaxRate> {
    return this.api
      .createTaxRate(mapTaxRateRequest(request))
      .pipe(map(mapTaxRate));
  }

  listCatalogs(activeOnly = true): Observable<DynamicCatalog[]> {
    return this.api
      .listCatalogs(activeOnly)
      .pipe(map((items) => items.map(mapCatalog)));
  }

  createCatalog(
    request: DynamicCatalogUpsertRequest,
  ): Observable<DynamicCatalog> {
    return this.api
      .createCatalog(mapCatalogRequest(request))
      .pipe(map(mapCatalog));
  }

  updateCatalog(
    catalogId: number,
    request: DynamicCatalogUpsertRequest,
  ): Observable<DynamicCatalog> {
    return this.api
      .updateCatalog(catalogId, mapCatalogRequest(request))
      .pipe(map(mapCatalog));
  }

  listCatalogItems(
    catalogId: number,
    activeOnly = true,
  ): Observable<DynamicCatalogItem[]> {
    return this.api
      .listCatalogItems(catalogId, activeOnly)
      .pipe(map((items) => items.map(mapCatalogItem)));
  }

  createCatalogItem(
    catalogId: number,
    request: DynamicCatalogItemUpsertRequest,
  ): Observable<DynamicCatalogItem> {
    return this.api
      .createCatalogItem(catalogId, mapCatalogItemRequest(request))
      .pipe(map(mapCatalogItem));
  }

  updateCatalogItem(
    catalogId: number,
    itemId: number,
    request: DynamicCatalogItemUpsertRequest,
  ): Observable<DynamicCatalogItem> {
    return this.api
      .updateCatalogItem(catalogId, itemId, mapCatalogItemRequest(request))
      .pipe(map(mapCatalogItem));
  }

  listCustomers(
    companyId: number,
    activeOnly = true,
  ): Observable<MasterDataCustomer[]> {
    return this.api
      .listCustomers(companyId, activeOnly)
      .pipe(map((items) => items.map(mapCustomer)));
  }

  createCustomer(
    request: CustomerUpsertRequest,
  ): Observable<MasterDataCustomer> {
    return this.api
      .createCustomer(mapCustomerRequest(request))
      .pipe(map(mapCustomer));
  }

  updateCustomer(
    customerId: number,
    request: CustomerUpsertRequest,
  ): Observable<MasterDataCustomer> {
    return this.api
      .updateCustomer(customerId, mapCustomerRequest(request))
      .pipe(map(mapCustomer));
  }

  deactivateCustomer(customerId: number): Observable<void> {
    return this.api.deactivateCustomer(customerId);
  }

  listProducts(
    companyId: number,
    activeOnly = true,
  ): Observable<MasterDataProduct[]> {
    return this.api
      .listProducts(companyId, activeOnly)
      .pipe(map((items) => items.map(mapProduct)));
  }

  createProduct(request: ProductUpsertRequest): Observable<MasterDataProduct> {
    return this.api
      .createProduct(mapProductRequest(request))
      .pipe(map(mapProduct));
  }

  updateProduct(
    productId: number,
    request: ProductUpsertRequest,
  ): Observable<MasterDataProduct> {
    return this.api
      .updateProduct(productId, mapProductRequest(request))
      .pipe(map(mapProduct));
  }

  deactivateProduct(productId: number): Observable<void> {
    return this.api.deactivateProduct(productId);
  }

  listSctr(type?: SctrType): Observable<SctrProvider[]> {
    return this.api.listSctr(type).pipe(map((items) => items.map(mapSctr)));
  }

  createSctr(request: SctrUpsertRequest): Observable<SctrProvider> {
    return this.api.createSctr(mapSctrRequest(request)).pipe(map(mapSctr));
  }

  updateSctr(
    sctrId: number,
    request: SctrUpsertRequest,
  ): Observable<SctrProvider> {
    return this.api
      .updateSctr(sctrId, mapSctrRequest(request))
      .pipe(map(mapSctr));
  }

  deleteSctr(sctrId: number): Observable<void> {
    return this.api.deleteSctr(sctrId);
  }

  listBankEntities(): Observable<BankEntity[]> {
    return this.api
      .listBankEntities()
      .pipe(map((items) => items.map(mapBankEntity)));
  }

  createBankEntity(request: BankEntityUpsertRequest): Observable<BankEntity> {
    return this.api
      .createBankEntity(mapBankEntityRequest(request))
      .pipe(map(mapBankEntity));
  }

  updateBankEntity(
    bankEntityId: number,
    request: BankEntityUpsertRequest,
  ): Observable<BankEntity> {
    return this.api
      .updateBankEntity(bankEntityId, mapBankEntityRequest(request))
      .pipe(map(mapBankEntity));
  }

  deleteBankEntity(bankEntityId: number): Observable<void> {
    return this.api.deleteBankEntity(bankEntityId);
  }

  listWorkerTypes(activeOnly = true): Observable<WorkerType[]> {
    return this.api
      .listWorkerTypes(activeOnly)
      .pipe(map((items) => items.map(mapWorkerType)));
  }

  createWorkerType(request: WorkerTypeUpsertRequest): Observable<WorkerType> {
    return this.api
      .createWorkerType(mapWorkerTypeRequest(request))
      .pipe(map(mapWorkerType));
  }

  updateWorkerType(
    workerTypeId: number,
    request: WorkerTypeUpsertRequest,
  ): Observable<WorkerType> {
    return this.api
      .updateWorkerType(workerTypeId, mapWorkerTypeRequest(request))
      .pipe(map(mapWorkerType));
  }

  listWorldLocations(countryCode?: string, regionCode?: string, provinceCode?: string): Observable<any> { return this.api.listWorldLocations(countryCode, regionCode, provinceCode); } createWorldLocation(request: any): Observable<any> { return this.api.createWorldLocation(request); } updateWorldLocation(id: number, request: any): Observable<any> { return this.api.updateWorldLocation(id, request); } deleteWorldLocation(id: number): Observable<void> { return this.api.deleteWorldLocation(id); } listRegions(countryCode: string): Observable<string[]> { return this.api.listRegions(countryCode); } listProvincesForWorldLocation(countryCode: string, regionCode: string): Observable<string[]> { return this.api.listProvincesForWorldLocation(countryCode, regionCode); } listDistrictsForWorldLocation(countryCode: string, regionCode: string, provinceCode: string): Observable<string[]> { return this.api.listDistrictsForWorldLocation(countryCode, regionCode, provinceCode); } deleteWorkerType(workerTypeId: number): Observable<void> {
    return this.api.deleteWorkerType(workerTypeId);
  }
}

